/**
 * How a pedal names the machine it is physically plugged into - the pure
 * decisions, so a smoke can drive them without a mesh.
 *
 * # The defect this exists to fix
 *
 * `GuitarPedal.bridgeId` held a **device-local row id** (`newBridgeId()` -
 * `br_` plus `Math.random()` plus a timestamp, `opbridge/storage.ts`). Pedals
 * live on `Project.pedals`, and `layout/projectSync.ts` pushes the WHOLE
 * `Project` as the document body. So a random id minted on ONE machine was
 * replicating mesh-wide inside a synced document.
 *
 * The consequence on the other machine: `resolveBridge` joins on `b.id ===
 * target`, finds nothing, `bridgeConnectedReactive` is false, and every
 * PresetChange / SnapshotChange tile greys with "no bridge" - on a machine
 * where the pedal is perfectly reachable. It also explains why the symptom
 * appeared somewhere other than where the pedal was added.
 *
 * **And it interacted badly with `pedals/rebind.ts`.** Each device rebound to
 * its OWN local id, `updatePedal` mutated the project, the doc-store pushed
 * it, the other device pulled it and its binding was then wrong. Not a
 * runaway - `attemptedFor` caps it - but a genuine last-writer-wins fight
 * that decided by coin flip which machine worked. Binding by `nodeId`
 * dissolves that: both machines now converge on the SAME value instead of
 * overwriting each other with different ones.
 *
 * # The rule
 *
 * Store the bridge's **stable `nodeId`**; resolve it to a local row at USE
 * time, never at store time. This is DEC-465's join rule applied one level
 * up - `PairedBridgeEntry.node_id`'s own doc says *"Do NOT join on
 * `instance_name` (unstable mDNS label)"*, and a device-local row id is worse
 * than an mDNS label because it is not merely unstable, it is meaningless off
 * the machine that minted it.
 *
 * # ⚠️ Why "unresolved" must not be the empty string
 *
 * The subtlest trap here, and it would have been a silent wrong-machine send.
 * `resolveBridge('')` and `resolveBridge(undefined)` both return
 * `primaryBridge()`, because "no binding" legitimately means "use the default
 * bridge" for a pedal that never chose one. So a binding that FAILED to
 * resolve must never collapse to `''` - that would silently retarget the
 * operator's default machine and light the tile green while doing it.
 * {@link UNRESOLVED_BRIDGE} is a non-empty sentinel that matches no row id,
 * so `resolveBridge` returns null and the tile reads "no bridge" honestly.
 */

import { findBridgeByNode } from '../opbridge/store.ts';
import type { GuitarPedal } from '../layout/projectStore.ts';

/**
 * Stands in for "this pedal names a machine we cannot currently identify".
 *
 * Deliberately non-empty and deliberately not a real id shape: `resolveBridge`
 * finds no row for it and returns null, which every caller already renders as
 * "not connected". The empty string would mean the DEFAULT bridge - see the
 * module header.
 */
export const UNRESOLVED_BRIDGE = '__opbuttons_unresolved_bridge__';

/**
 * The LOCAL bridge row id this pedal should dispatch to, or `undefined` for
 * "no binding, use the default bridge".
 *
 * Three cases, in order:
 *
 * 1. **`bridgeNodeId` set and resolvable** - the local row carrying that
 *    node. This is the post-migration path and the only one that is correct
 *    across the mesh.
 * 2. **`bridgeNodeId` set, no local row carries it** - {@link
 *    UNRESOLVED_BRIDGE}. This is a REAL and temporary state, not an error:
 *    `nodeId` is backfilled by `reportBridgeIdentity` on the first successful
 *    `/health`, so a freshly-paired row legitimately has `nodeId: null` for a
 *    while. It degrades to "not connected" and never throws.
 * 3. **No `bridgeNodeId`** - fall back to the legacy `bridgeId`. Pre-migration
 *    pedals, and pedals whose row has not yet reported a node. `undefined`
 *    here preserves the documented "absent = the default bridge" behaviour.
 */
export function pedalBridgeRowId(pd: GuitarPedal | undefined): string | undefined {
  if (!pd) return undefined;
  if (pd.bridgeNodeId) {
    return findBridgeByNode(pd.bridgeNodeId)?.id ?? UNRESOLVED_BRIDGE;
  }
  return pd.bridgeId;
}

/**
 * The patch that migrates one pedal from a local row id to a node id, or
 * `null` when it needs none.
 *
 * Pure so the migration is testable without a store. Returns a patch rather
 * than mutating, so the caller owns when and whether it lands.
 *
 * **`bridgeId` is deliberately KEPT, not cleared.** Three reasons, and the
 * third is the one that matters: it costs almost nothing; an older build of
 * the app reading the same synced document still finds its binding; and
 * clearing it would make this migration destructive on a document that other
 * machines are concurrently reading, for no gain - {@link pedalBridgeRowId}
 * already ignores it whenever `bridgeNodeId` is present.
 *
 * **Returns null when the row has no `nodeId` yet.** That is not a failure -
 * the backfill simply has not happened. Leaving the pedal on its legacy
 * binding until then is strictly better than writing a node id we made up,
 * and the next sweep picks it up for free.
 */
export function pedalNodeMigration(
  pd: GuitarPedal,
  rowNodeId: (rowId: string) => string | null,
): { bridgeNodeId: string } | null {
  if (pd.bridgeNodeId) return null;      // already migrated
  if (!pd.bridgeId) return null;         // no binding to migrate
  const node = rowNodeId(pd.bridgeId);
  if (!node) return null;                // row unknown here, or not yet backfilled
  return { bridgeNodeId: node };
}

/**
 * Is this pedal's binding one that cannot currently be resolved?
 *
 * Distinct from "not connected": a pedal whose node resolves to a row that is
 * merely offline is NOT unresolved. Used to keep the rebind sweep from
 * thrashing while a `nodeId` backfill is still pending.
 */
export function pedalBindingUnresolved(pd: GuitarPedal | undefined): boolean {
  return pedalBridgeRowId(pd) === UNRESOLVED_BRIDGE;
}
